import Phaser from 'phaser';
import type {
  DecisionOption,
  Mission,
  MissionStep,
  ObjetoExplorable,
} from '@/data/types';
import { getCharacter } from '@/data/personajes';
import { getMission } from '@/data/misiones';
import { GameState } from '@/engine/GameState';
import { drawPortrait } from '@/engine/portrait';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  type MissionSceneData,
} from '@/engine/nav';

/** Evento global (game.events) que las escenas de minijuego emiten al
 *  terminar. MissionScene lo escucha para reanudar y aplicar el resultado.
 *  Contrato: el minijuego debe emitir `{ exito: boolean }` y luego
 *  `scene.stop()` para liberar la escena. */
const MINIJUEGO_TERMINADO = 'minijuego-terminado';

// Paleta y constantes de layout. Coherente con PlaceholderScene / HubScene.
const COLOR_FONDO = 0x14141c;
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_SELECCIONADO = 0xf1c40f; // borde amarillo para tarjeta marcada
const COLOR_SELECCIONADO_DIM = 0x6b5e10; // borde apagado para tarjeta no marcada
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';

const HUD_ALTO = 100; // debe coincidir con HudScene
const MARGEN = 24;

/** Botín temporal acumulado durante la misión: exploraciones suman aquí
 *  y se vuelcan a GameState en el paso de recompensa. */
interface BotinTemporal {
  dinero: number;
  respeto: number;
  /** Suma de riesgos (0-100) de los objetos recogidos — la misión lo usará. */
  riesgo: number;
}

/**
 * Escena genérica de misión. Recibe `{ missionId }`, carga la Mission
 * desde el catálogo (src/data/misiones.ts) y ejecuta sus `steps` en
 * orden: dialogo, decision, exploracion, minijuego, recompensa.
 *
 * Separación dura: el motor (esta escena) NO sabe de historia — solo
 * renderiza los datos que vienen de `Mission`. Cero texto narrativo aquí.
 */
export class MissionScene extends Phaser.Scene {
  private missionId!: string;
  private mission: Mission | null = null;

  /** Índice del step actual dentro de mission.steps. */
  private currentStepIndex = 0;
  /** Índice dentro de las líneas del diálogo actual (cuando el step es dialogo). */
  private currentDialogueLineIndex = 0;

  private botin: BotinTemporal = { dinero: 0, respeto: 0, riesgo: 0 };
  /** Objetos marcados en el paso de exploración actual. */
  private objetosSeleccionados: Set<string> = new Set();

  /** Contenedor donde vive todo el UI del step actual. */
  private uiContainer!: Phaser.GameObjects.Container;

  /** Si la mission no se pudo cargar, este flag activa el panel de error. */
  private loadError: string | null = null;

  constructor() {
    super(SCENE_KEYS.MISSION);
  }

  init(data: MissionSceneData): void {
    this.missionId = data.missionId;
    // Estado limpio por si la escena se reutiliza entre misiones.
    this.currentStepIndex = 0;
    this.currentDialogueLineIndex = 0;
    this.botin = { dinero: 0, respeto: 0, riesgo: 0 };
    this.objetosSeleccionados.clear();
    this.loadError = null;
    this.mission = null;

    try {
      this.mission = getMission(this.missionId);
    } catch (err) {
      this.loadError = err instanceof Error ? err.message : String(err);
      console.error('[MissionScene]', this.loadError);
    }
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.uiContainer = this.add.container(0, 0);

    // Si la escena se cierra con un minijuego a medias, el listener global
    // quedaría colgado y dispararía sobre una escena muerta.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off(MINIJUEGO_TERMINADO, this.handleMinijuegoTerminado, this);
    });

    if (this.loadError) {
      this.renderError(width, height);
      return;
    }
    if (!this.mission || this.mission.steps.length === 0) {
      this.renderError(width, height, 'La misión no tiene pasos.');
      return;
    }

    this.renderStep();
  }

  // ---------------------------------------------------------------------
  // Render de steps
  // ---------------------------------------------------------------------

  private renderStep(): void {
    if (!this.mission) return;
    const step = this.mission.steps[this.currentStepIndex];
    if (!step) {
      // Sin más pasos: la recompensa debería haber vuelto al hub, pero
      // si llegamos aquí por un branch raro, salimos limpios.
      this.irAlHub();
      return;
    }
    this.limpiarUI();

    switch (step.tipo) {
      case 'dialogo':
        this.renderDialogo(step);
        break;
      case 'decision':
        this.renderDecision(step);
        break;
      case 'exploracion':
        this.objetosSeleccionados.clear();
        this.renderExploracion(step);
        break;
      case 'minijuego':
        this.renderMinijuego(step);
        break;
      case 'recompensa':
        this.renderRecompensa(step);
        break;
    }
  }

  /** Avanza al siguiente step o salta al índice dado por una decisión. */
  private avanzarA(indice?: number): void {
    if (typeof indice === 'number') {
      this.currentStepIndex = indice;
    } else {
      this.currentStepIndex += 1;
    }
    // Si el step entrante es diálogo, empezamos desde la primera línea.
    const nuevo = this.mission?.steps[this.currentStepIndex];
    if (nuevo?.tipo === 'dialogo') {
      this.currentDialogueLineIndex = 0;
    }
    this.renderStep();
  }

  // --- Dialogo --------------------------------------------------------

  private renderDialogo(step: Extract<MissionStep, { tipo: 'dialogo' }>): void {
    const { width, height } = this.scale;

    // Caja inferior grande con retrato + apodo + texto + indicador ▼.
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 300;
    const panelCentroY = height - panelAlto / 2 - 30;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 1);
    panel.fillRoundedRect(
      -panelAncho / 2,
      -panelAlto / 2,
      panelAncho,
      panelAlto,
      20,
    );
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(
      -panelAncho / 2,
      -panelAlto / 2,
      panelAncho,
      panelAlto,
      20,
    );
    panel.setPosition(width / 2, panelCentroY);
    this.uiContainer.add(panel);

    // Retrato del hablante a la izquierda (usa el portrait.ts genérico).
    const retratoX = width / 2 - panelAncho / 2 + 70;
    const retratoY = panelCentroY - 30;
    const linea = step.lineas[this.currentDialogueLineIndex];
    if (linea) {
      // El retrato debe vivir dentro de uiContainer: limpiarUI() lo destruye
      // al re-renderizar; si no, cada avance de línea dejaría un retrato
      // fantasma acumulado en la escena.
      const retrato = drawPortrait(this, retratoX, retratoY, linea.personajeId, linea.emocion, {
        mostrarApodo: false,
      });
      this.uiContainer.add(retrato);
    }

    // Apodo del hablante (en su color) y texto de la línea, a la derecha.
    const textoX = width / 2 - panelAncho / 2 + 140;
    const textoAncho = panelAncho - 140 - 30;
    if (linea) {
      const char = getCharacter(linea.personajeId);
      const colorHex =
        '#' + (char.color ?? 0x4a4a55).toString(16).padStart(6, '0');
      this.uiContainer.add(
        this.add.text(textoX, panelCentroY - 70, char.apodo, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: colorHex,
        }),
      );
      this.uiContainer.add(
        this.add.text(textoX, panelCentroY - 34, linea.texto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
          wordWrap: { width: textoAncho },
        }),
      );
    }

    // Indicador ▼ abajo a la derecha (si hay más líneas o más pasos).
    const hayMasLineas = this.currentDialogueLineIndex < step.lineas.length - 1;
    const totalSteps = this.mission?.steps.length ?? 0;
    const esUltimoStep = this.currentStepIndex >= totalSteps - 1;
    if (hayMasLineas || !esUltimoStep) {
      this.uiContainer.add(
        this.add
          .text(
            width / 2 + panelAncho / 2 - 30,
            panelCentroY + panelAlto / 2 - 24,
            '▼',
            {
              fontFamily: 'system-ui, sans-serif',
              fontSize: '22px',
              color: COLOR_TEXTO_DIM,
            },
          )
          .setOrigin(0.5),
      );
    }

    // Zona tap-a-cualquier-sitio que avanza línea a línea. Cubre toda la
    // pantalla y queda por encima del panel.
    const zonaTap = this.add
      .zone(width / 2, height / 2, width, height)
      .setInteractive({ useHandCursor: true });
    zonaTap.on('pointerup', () => this.avanzarDialogo(step));
    this.uiContainer.add(zonaTap);
  }

  /** Avanza una línea de diálogo, o al siguiente step si era la última. */
  private avanzarDialogo(step: Extract<MissionStep, { tipo: 'dialogo' }>): void {
    if (this.currentDialogueLineIndex < step.lineas.length - 1) {
      this.currentDialogueLineIndex += 1;
      // Re-render completo del panel para que retrato, texto e indicador
      // reflejen la nueva línea sin necesidad de buscar hijos.
      this.renderStep();
    } else {
      this.avanzarA();
    }
  }

  // --- Decision --------------------------------------------------------

  private renderDecision(step: Extract<MissionStep, { tipo: 'decision' }>): void {
    const { width, height } = this.scale;

    // Pregunta arriba.
    this.uiContainer.add(
      this.add
        .text(width / 2, HUD_ALTO + 60, step.pregunta, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: width - MARGEN * 2 },
        })
        .setOrigin(0.5),
    );

    // Botones grandes apilados.
    const opciones = step.opciones;
    const areaTop = HUD_ALTO + 130;
    const areaBottom = height - MARGEN - 60;
    const areaAlto = areaBottom - areaTop;
    const gap = 14;
    const botonAlto = Math.min(
      84,
      (areaAlto - gap * (opciones.length - 1)) / opciones.length,
    );
    const botonAncho = width - MARGEN * 2;
    opciones.forEach((opcion, i) => {
      const y = areaTop + i * (botonAlto + gap) + botonAlto / 2;
      this.crearBotonOpcion(opcion, width / 2, y, botonAncho, botonAlto);
    });
  }

  private crearBotonOpcion(
    opcion: DecisionOption,
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x, y, opcion.texto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: ancho - 32 },
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      this.aplicarDecision(opcion);
    });
    this.uiContainer.add(zona);
  }

  /** Aplica los efectos de la opción elegida y avanza al siguiente paso. */
  private aplicarDecision(opcion: DecisionOption): void {
    opcion.setFlags?.forEach((flag) => GameState.setFlag(flag, true));
    if (opcion.respeto) GameState.addRespeto(opcion.respeto);
    this.avanzarA(opcion.siguientePasoIndex);
  }

  // --- Exploracion ----------------------------------------------------

  private renderExploracion(
    step: Extract<MissionStep, { tipo: 'exploracion' }>,
  ): void {
    const { width, height } = this.scale;

    // Descripción arriba.
    this.uiContainer.add(
      this.add
        .text(width / 2, HUD_ALTO + 50, step.descripcion, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO_DIM,
          align: 'center',
          wordWrap: { width: width - MARGEN * 2 },
        })
        .setOrigin(0.5),
    );

    // Grid 2 columnas flexible. Para 1-3 objetos usa 1 fila, para más usa
    // varias filas ajustando la altura de cada tarjeta.
    const objetos = step.objetos;
    const cols = objetos.length <= 3 ? objetos.length : 2;
    const filas = Math.ceil(objetos.length / cols);

    const gridTop = HUD_ALTO + 110;
    const gridBottom = height - 140; // deja sitio al botón COGER
    const gridAlto = gridBottom - gridTop;
    const gap = 14;
    const cardAncho = (width - MARGEN * 2 - gap * (cols - 1)) / cols;
    const cardAlto = (gridAlto - gap * (filas - 1)) / filas;

    objetos.forEach((obj, i) => {
      const col = i % cols;
      const fila = Math.floor(i / cols);
      const x = MARGEN + cardAncho / 2 + col * (cardAncho + gap);
      const y = gridTop + cardAlto / 2 + fila * (cardAlto + gap);
      this.crearTarjetaObjeto(obj, x, y, cardAncho, cardAlto);
    });

    // Botón COGER LO MARCADO abajo.
    this.crearBotonCoger(width / 2, gridBottom + 35);
  }

  private crearTarjetaObjeto(
    obj: ObjetoExplorable,
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const card = this.add.graphics();
    const dibujar = (seleccionado: boolean) => {
      card.clear();
      card.fillStyle(COLOR_PANEL, 1);
      card.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 16);
      card.lineStyle(3, seleccionado ? COLOR_SELECCIONADO : COLOR_SELECCIONADO_DIM, 1);
      card.strokeRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 16);
    };
    dibujar(this.objetosSeleccionados.has(obj.id));
    card.setPosition(x, y);
    this.uiContainer.add(card);

    // Emoji grande arriba.
    this.uiContainer.add(
      this.add
        .text(x, y - alto / 2 + 36, obj.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '40px',
        })
        .setOrigin(0.5),
    );

    // Nombre debajo.
    this.uiContainer.add(
      this.add
        .text(x, y - alto / 2 + 86, obj.nombre, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: ancho - 16 },
        })
        .setOrigin(0.5),
    );

    // Descripción breve más abajo.
    this.uiContainer.add(
      this.add
        .text(x, y + 18, obj.descripcion, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
          align: 'center',
          wordWrap: { width: ancho - 20 },
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerup', () => {
      if (this.objetosSeleccionados.has(obj.id)) {
        this.objetosSeleccionados.delete(obj.id);
      } else {
        this.objetosSeleccionados.add(obj.id);
      }
      dibujar(this.objetosSeleccionados.has(obj.id));
    });
    this.uiContainer.add(zona);
  }

  private crearBotonCoger(x: number, y: number): void {
    const { width } = this.scale;
    const anchoBoton = width - MARGEN * 2;
    const altoBoton = 80;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x, y, 'COGER LO MARCADO', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(x, y, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.cogerMarcados());
    this.uiContainer.add(zona);
  }

  /** Aplica los objetos marcados al botín temporal y avanza al siguiente step. */
  private cogerMarcados(): void {
    if (!this.mission) return;
    const step = this.mission.steps[this.currentStepIndex];
    if (step.tipo !== 'exploracion') return;

    step.objetos.forEach((obj) => {
      if (!this.objetosSeleccionados.has(obj.id)) return;
      this.botin.dinero += obj.dinero ?? 0;
      this.botin.respeto += obj.respeto ?? 0;
      this.botin.riesgo += obj.riesgo ?? 0;
      obj.setFlags?.forEach((flag) => GameState.setFlag(flag, true));
    });
    this.objetosSeleccionados.clear();
    this.avanzarA();
  }

  // --- Minijuego -------------------------------------------------------

  private renderMinijuego(step: Extract<MissionStep, { tipo: 'minijuego' }>): void {
    const { width, height } = this.scale;
    const sceneManager = this.scene.manager;

    if (sceneManager.keys[step.minijuegoId]) {
      // Pausamos esta escena para que el minijuego tome el input.
      // Escuchamos el evento global que el minijuego debe emitir al
      // terminar (ver constante MINIJUEGO_TERMINADO).
      this.game.events.once(MINIJUEGO_TERMINADO, this.handleMinijuegoTerminado, this);
      this.scene.pause();
      this.scene.launch(step.minijuegoId);
      return;
    }

    // Placeholder cuando el minijuego aún no está implementado.
    this.renderPlaceholderMinijuego(step.minijuegoId, width, height);
  }

  private renderPlaceholderMinijuego(minijuegoId: string, width: number, height: number): void {
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 280;
    const cx = width / 2;
    const cy = height / 2 - 20;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 1);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(cx, cy);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(cx, cy - 70, 'Minijuego próximamente', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(cx, cy - 30, `(${minijuegoId})`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    const anchoBoton = 260;
    const altoBoton = 70;
    const botonY = cy + 60;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(cx, botonY, 'CONTINUAR', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(cx, botonY, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      // El placeholder cuenta como éxito (la misión aún no exige resultado).
      this.handleMinijuegoTerminado({ exito: true });
    });
    this.uiContainer.add(zona);
  }

  /** Manejador del evento de fin de minijuego: reanuda la escena y avanza. */
  private handleMinijuegoTerminado(_data: { exito: boolean }): void {
    // Si veníamos del placeholder no estamos pausados: solo avanzamos.
    if (this.scene.isPaused()) {
      this.scene.resume();
    }
    // El campo exito se ignora en este bloque — el contenido real de las
    // misiones lo usará para ramificar.
    void _data;
    this.avanzarA();
  }

  // --- Recompensa ------------------------------------------------------

  private renderRecompensa(step: Extract<MissionStep, { tipo: 'recompensa' }>): void {
    if (!this.mission) return;

    const totalRespeto = step.respeto + this.botin.respeto;
    const totalDinero = (step.dinero ?? 0) + this.botin.dinero;

    // Volcar al GameState y marcar misión completada.
    if (totalRespeto) GameState.addRespeto(totalRespeto);
    if (totalDinero) GameState.addDinero(totalDinero);
    GameState.marcarMisionCompletada(this.mission.id);

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2 - 40;

    // Título.
    this.uiContainer.add(
      this.add
        .text(cx, cy - 140, this.mission.titulo, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: width - MARGEN * 2 },
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(cx, cy - 100, 'Misión completada', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    // Resumen central: ✊ +X · 💶 +Y (y riesgo acumulado si hay).
    const partes: string[] = [];
    if (totalRespeto) partes.push(`✊ +${totalRespeto}`);
    if (totalDinero) partes.push(`💶 +${totalDinero}`);
    const resumen = partes.length > 0 ? partes.join('  ·  ') : 'Sin recompensas';
    this.uiContainer.add(
      this.add
        .text(cx, cy, resumen, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '38px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: width - MARGEN * 2 },
        })
        .setOrigin(0.5),
    );

    if (this.botin.riesgo > 0) {
      this.uiContainer.add(
        this.add
          .text(cx, cy + 70, `⚠️ riesgo acumulado: ${this.botin.riesgo}`, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '18px',
            color: COLOR_TEXTO_DIM,
          })
          .setOrigin(0.5),
      );
    }

    // Botón VOLVER.
    const anchoBoton = 280;
    const altoBoton = 76;
    const botonY = cy + 160;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(cx, botonY, 'VOLVER', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(cx, botonY, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.irAlHub());
    this.uiContainer.add(zona);
  }

  // --- Error / navegación ---------------------------------------------

  private renderError(width: number, height: number, mensaje?: string): void {
    const cx = width / 2;
    const cy = height / 2;
    this.uiContainer.add(
      this.add
        .text(cx, cy - 40, 'No se puede cargar la misión', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: width - MARGEN * 2 },
        })
        .setOrigin(0.5),
    );
    if (mensaje) {
      this.uiContainer.add(
        this.add
          .text(cx, cy, mensaje, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '16px',
            color: COLOR_TEXTO_DIM,
            align: 'center',
            wordWrap: { width: width - MARGEN * 2 },
          })
          .setOrigin(0.5),
      );
    }

    const anchoBoton = 240;
    const altoBoton = 70;
    const botonY = cy + 100;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(cx, botonY, '← Volver al barrio', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(cx, botonY, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.irAlHub());
    this.uiContainer.add(zona);
  }

  /** Limpia el UI del step actual (contenedor + cualquier zona interactiva). */
  private limpiarUI(): void {
    if (!this.uiContainer) return;
    // removeAll(true) destruye también los hijos: textos, gráficos, zones.
    this.uiContainer.removeAll(true);
  }

  private irAlHub(): void {
    goTo(this, SCENE_KEYS.HUB);
  }
}